/**
 * WorkloadAssumptionsTable — a collapsible, read-only comparison of the KEY
 * PLANNING ASSUMPTIONS behind each workload template, surfaced in the intent
 * Workload step directly beneath the WorkloadProfilePicker.
 *
 * PURPOSE: make the developer-economics differences between templates legible
 * at the moment of choosing one — in particular whether two templates that look
 * alike at the headline (AI Training vs Multi-tenant Colocation, both ~1 GW,
 * both ~$300k/MW dev fee) actually carry the same underlying risk and revenue
 * mechanics. They don't, and each row's note says why.
 *
 * DATA SOURCES (kept SSOT — nothing is duplicated):
 *   - label + power-density anchor  → WORKLOAD_PROFILES        (workloadProfile.ts)
 *   - commercial assumption columns → WORKLOAD_COMMERCIAL_ASSUMPTIONS
 *                                     (workloadAssumptions.ts) — INDICATIVE,
 *                                     does NOT feed scoring / LCOE.
 *
 * The three templates in the current AI-training vs inference vs colocation
 * comparison get a subtle accent so the eye lands on them first; the component
 * is otherwise a pure function of the two catalogs, so extending either lights
 * up here with no edit.
 */
import React from 'react';
import { WORKLOAD_PROFILES, type WorkloadProfile } from '../../lib/workloadProfile';
import { WORKLOAD_COMMERCIAL_ASSUMPTIONS } from '../../lib/workloadAssumptions';

/** Templates called out in the current developer comparison (accent only). */
const HIGHLIGHTED: ReadonlySet<WorkloadProfile> = new Set<WorkloadProfile>([
  'ai-training',
  'ai-inference',
  'colocation',
]);

interface Column {
  key: string;
  header: string;
  /** Right-aligned, tabular-nums columns (the numeric-ish anchors). */
  numeric?: boolean;
}

const COLUMNS: ReadonlyArray<Column> = [
  { key: 'template', header: 'Workload template' },
  { key: 'scale', header: 'Typical single-site scale' },
  { key: 'density', header: 'Density (kW/rack)', numeric: true },
  { key: 'customer', header: 'Primary end-customer' },
  { key: 'offtake', header: 'Offtake / contract' },
  { key: 'revenue', header: 'Revenue model' },
  { key: 'devFee', header: 'Indicative dev fee', numeric: true },
];

export function WorkloadAssumptionsTable(): React.ReactElement {
  return (
    <details
      className="mt-2 rounded-lg border border-border-subtle bg-state-hover/30"
      data-testid="workload-assumptions-table"
    >
      <summary className="cursor-pointer select-none px-3 py-2 type-meta font-semibold text-text-secondary hover:text-text-primary">
        Compare key assumptions by workload template
      </summary>

      <div className="px-3 pb-3">
        <p className="mb-2 text-[10px] italic leading-relaxed text-text-muted normal-case tracking-normal">
          Indicative planning assumptions for a US greenfield play (2026) — order-of-magnitude,
          developer-facing. These are reference figures for <em>choosing</em> a template; they do NOT
          feed a site's LCOE or scoring (those come from its Financial Assumptions). Confirm the
          dev-fee column against your live deal model before quoting it.
        </p>

        <div className="overflow-x-auto">
          <table
            className="w-full border-collapse text-left"
            data-testid="workload-assumptions-grid"
          >
            <thead>
              <tr className="border-b border-border-subtle">
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    className={`type-meta whitespace-nowrap px-2 py-1.5 align-bottom font-bold text-text-muted ${
                      c.numeric ? 'text-right' : ''
                    }`}
                  >
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {WORKLOAD_PROFILES.map((profile) => {
                const a = WORKLOAD_COMMERCIAL_ASSUMPTIONS[profile.id];
                const highlighted = HIGHLIGHTED.has(profile.id);
                const density = `${profile.powerDensityKwRack.min}–${profile.powerDensityKwRack.max}`;
                const accent = highlighted ? 'border-l-2 border-primary' : '';
                return (
                  <React.Fragment key={profile.id}>
                    <tr
                      data-testid={`workload-assumptions-row-${profile.id}`}
                      data-highlighted={highlighted || undefined}
                      className={`border-b border-border-subtle/60 align-top ${highlighted ? 'bg-primary/5' : ''}`}
                    >
                      <th
                        scope="row"
                        className={`whitespace-nowrap px-2 py-2 text-[11px] font-bold text-text-primary ${accent}`}
                      >
                        {profile.label}
                      </th>
                      <td className="px-2 py-2 text-[11px] text-text-secondary">{a.typicalScale}</td>
                      <td className="whitespace-nowrap px-2 py-2 text-right text-[11px] tabular-nums text-text-secondary">
                        {density}
                      </td>
                      <td className="px-2 py-2 text-[11px] text-text-secondary">{a.endCustomer}</td>
                      <td className="px-2 py-2 text-[11px] text-text-secondary">{a.offtake}</td>
                      <td className="px-2 py-2 text-[11px] text-text-secondary">{a.revenueModel}</td>
                      <td className="whitespace-nowrap px-2 py-2 text-right text-[11px] font-bold tabular-nums text-text-primary">
                        {a.devFeePerMw}
                      </td>
                    </tr>
                    {/* Honest "why it does / doesn't converge" caption, spanning the row. */}
                    <tr className={highlighted ? 'bg-primary/5' : ''}>
                      <td
                        colSpan={COLUMNS.length}
                        className={`px-2 pb-2 pt-0 text-[10px] italic leading-relaxed text-text-muted ${accent}`}
                      >
                        {a.developerNote}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="mt-2 text-[10px] italic leading-relaxed text-text-muted normal-case tracking-normal">
          Highlighted rows are the templates in the current AI-training vs inference vs colocation
          comparison. The same MW and the same $/MW dev fee can describe very different deals — the
          difference is in the counterparty, the achievable single-site scale, and lease-up risk
          (see each row's note).
        </p>
      </div>
    </details>
  );
}
